<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import CheckBox from '@/components/common/ui/inputs/CheckBox.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import FormSelect from '@/inputs/FormSelect.vue'
import { translationFunctionDefault } from '@/utilities/translations'

interface SectionBankAccountOrganizationProps {
  bankOptions: DropdownOption<string>[]
  compactPadding?: boolean
  // IBAN error message can come from backend validation.
  ibanErrorMessage?: string | undefined
  inCreateContext?: boolean
  isCollapsible?: boolean
  showTitle?: boolean
  withPrimary?: boolean

  onSubmit?(): void
}

const props = withDefaults(defineProps<SectionBankAccountOrganizationProps>(), {
  compactPadding: false,
  isCollapsible: true,
  onSubmit: () => undefined,
  showTitle: true,
  withPrimary: false,
})

const tfDefault = translationFunctionDefault()
const bankSelectOptions = computed(() => {
  return [props.bankOptions]
})

const { value: isDefault } = useField<number>('is_default')

const changeIsDefault = (): void => {
  isDefault.value = isDefault.value === 1 ? 0 : 1
}
</script>

<template>
  <CollapsableContent
    v-if="props.showTitle"
    :is-collapsible="props.isCollapsible"
    :title="tfDefault('editOrganization.bankAccountSectionTitle')"
  >
    <InputGrid :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.root">
      <TextInput
        :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.ownerInput"
        :in-create-context="inCreateContext ?? false"
        :label="tfDefault('editOrganization.bankAccountOwner')"
        name="tw_bank_account_owner"
        on-white-bg
        @blur="onSubmit"
      />
      <FormSelect
        :in-create-context="inCreateContext ?? false"
        label="Bank"
        name="tw_bank"
        on-white-bg
        :options="bankSelectOptions"
        searchable
        :test-id="TEST_IDS_CREATE.sectionBankAccountOrganization.bankSelect"
        @blur="onSubmit"
      />
      <TextInput
        :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.ibanInput"
        :error-message="props.ibanErrorMessage"
        :in-create-context="inCreateContext ?? false"
        :label="tfDefault('editOrganization.bankAccountIban')"
        name="tw_bank_account_iban"
        on-white-bg
        :placeholder="tfDefault('editOrganization.bankAccountIbanPlaceholder')"
        @blur="onSubmit"
      />
      <TextInput
        :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.bicInput"
        :in-create-context="inCreateContext ?? false"
        :label="tfDefault('editOrganization.bankAccountBic')"
        name="tw_bank_account_bic"
        on-white-bg
        @blur="onSubmit"
      />
    </InputGrid>
  </CollapsableContent>
  <section
    v-else
    class="w-full border-b border-dotted border-b-black-200 last-of-type:border-none"
    :class="props.compactPadding ? 'px-0 py-4' : 'px-8 py-5'"
  >
    <InputGrid :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.root">
      <TextInput
        :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.ownerInput"
        :label="tfDefault('editOrganization.bankAccountOwner')"
        name="tw_bank_account_owner"
        on-white-bg
        @blur="onSubmit"
      />
      <FormSelect
        label="Bank"
        name="tw_bank"
        on-white-bg
        :options="bankSelectOptions"
        searchable
        :test-id="TEST_IDS_CREATE.sectionBankAccountOrganization.bankSelect"
        @blur="onSubmit"
      />
      <TextInput
        :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.ibanInput"
        :error-message="props.ibanErrorMessage"
        :label="tfDefault('editOrganization.bankAccountIban')"
        name="tw_bank_account_iban"
        on-white-bg
        :placeholder="tfDefault('editOrganization.bankAccountIbanPlaceholder')"
        @blur="onSubmit"
      />
      <TextInput
        :data-testid="TEST_IDS_CREATE.sectionBankAccountOrganization.bicInput"
        :label="tfDefault('editOrganization.bankAccountBic')"
        name="tw_bank_account_bic"
        on-white-bg
        @blur="onSubmit"
      />
    </InputGrid>
    <CheckBox
      v-if="withPrimary"
      id="is_primary_address"
      :checked="isDefault === 1"
      class="mt-3"
      :label-text="tfDefault('editOrganization.bankAccountIsDefault')"
      on-white-bg
      @change="changeIsDefault"
    />
  </section>
</template>
